/**
 * DriftBanner — surfaces per-field drift between Nebari and FareHarbor's
 * warehouse snapshot (port of the design's NBDriftBanner, nb-token styled).
 * Nebari is authoritative; this is a read-only nudge, checked nightly.
 *
 * `role="status"` (polite) rather than `alert` (assertive): a live region only
 * announces a mutation to a region already in the accessibility tree, so an `alert`
 * present on first paint usually announces nothing, and "stop what you are doing"
 * is the wrong register for a standing observation about a venue. Changed alongside
 * TaxRateBanner (BON-387), which renders directly above this and would otherwise be
 * a second competing assertive region on the same page.
 */
import type { VenueDriftEntry } from '../../../../api/venues';

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function label(field: string): string {
  if (field === '_warehouseRecord') return 'FareHarbor record';
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export function DriftBanner({ drift }: { drift: VenueDriftEntry[] }) {
  if (drift.length === 0) return null;
  return (
    <section
      className="nb-card"
      role="status"
      style={{ flex: '0 0 auto', borderLeft: '3px solid var(--nb-warn)', padding: '12px 16px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--nb-ink)' }}>
          FareHarbor drift detected
        </span>
        {drift.map((d) => (
          <span key={d.field} className="nb-mono" style={{ fontSize: 11, color: 'var(--nb-warn)' }}>
            {label(d.field)}: Nebari <strong>{fmt(d.nebariValue)}</strong> · FH{' '}
            <strong>{fmt(d.fhValue)}</strong>
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}>
          Nebari is authoritative · checked nightly
        </span>
      </div>
    </section>
  );
}
